import React, {
    Children,
    cloneElement,
    ForwardRefExoticComponent,
    ReactElement,
    SVGProps,
    useEffect,
    useState
} from 'react'
import {ChevronDown, ChevronRight} from 'react-bootstrap-icons';
import Collapse from 'react-bootstrap/Collapse';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import {NavLink} from "react-router-dom";
import './SideBar.scss'

interface SideBarDropdownProps {
    title : string;
    forceClose? : boolean;
    children : React.ReactNode;
    icon?: ReactElement<SVGProps<never>>;
}

/**
 * Checks if the given ReactNode contains any `SideBarItem` components, either directly or nested within its children.
 *
 * @param {React.ReactNode} children - The ReactNode to search for `SideBarItem` components.
 * @return {boolean} Returns true if any `SideBarItem` components are found, otherwise false.
 */
function containsSideBarItems(children: React.ReactNode): boolean
{
    let result = false;

    React.Children.forEach(children, (child) =>
    {
        if (!React.isValidElement(child)) return;

        // Check if the child is a `SideBarItem` component
        if (child.type === SideBarItem) {
            result = true;
        } else if (child.props?.children) {
            // Recursively check nested children
            result = result || containsSideBarItems(child.props.children);
        }
    });

    return result;
}

export const SideBarDropdown = ({title, children, forceClose, icon} : SideBarDropdownProps) => {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (forceClose) {
            setOpen(false);
        }
    }, [forceClose])

    return !containsSideBarItems(children) ? null : (
        <Container fluid className="sidebar-dropdown">
            <Nav.Item onClick={() => setOpen(!open)} className="nav-link navbar-brand user-select-none ps-2 ms-1 pe-0 d-flex align-items-center">
                <span className="sidebar-dropdown-title w-100 d-flex align-items-center mt-1 mb-1">{icon}<span className="me-2"/>{title} </span> {open ? <ChevronDown className="float-end" size="0.8rem" /> :
                                <ChevronRight className="float-end" size="0.8rem" />}
            </Nav.Item>
            <Collapse in={open}>
                <div>
                    {Children.map(children as React.ReactElement , (child) =>
                        child !== null && cloneElement(child, {
                            forceClose: !open
                        })
                    )}
                </div>
            </Collapse>
        </Container>
    )
}

interface SideBarItemProps {
    title : string;
    link : string;
    icon?: ReactElement<ForwardRefExoticComponent<Omit<SVGProps<SVGSVGElement>, string | number | symbol>>>;
}

export const SideBarItem = ({title, link, icon} : SideBarItemProps) => {
    return (
        <Container fluid>
            <Nav.Item className="nav-pills user-select-none sidebar-item mt-1 mb-1">
                {/*
                    NavLink will add the active class if the browser path matches the path set in the to property
                    See https://reactrouter.com/en/main/components/nav-link
                */}
                <NavLink to={link} className="nav-link navbar-brand ps-2 ms-1 pe-0"><div className="d-flex align-items-center sidebar-item-title">{icon}<span className="me-2"/>{title}</div></NavLink>
            </Nav.Item>
        </Container>
    )
}

interface SideBarProps {
    children : React.ReactNode;
    className?: string;
}

export const SideBar = ({children, className} : SideBarProps) => {
    return <Nav className={`flex-column sidebar-nav ${className}`}>{children}</Nav>
}

SideBar.Item = SideBarItem;
SideBar.Dropdown = SideBarDropdown;